import React, { useEffect, useMemo } from "react";
import { Card, CardProps, Tooltip, Typography } from "antd";
import { HoverableCard } from "components/HoverableCard";
import { radius } from "@libertify/ui";

export const PICTURE_PREVIEW_WIDTH = 200;

interface ImagePreviewCardProps extends CardProps {
  image: string | File;
  fileName: string;
  cardWidth?: number | string;
  coverHeight?: number | string;
  onCardClick?: () => void;
  meta?: boolean;
  refreshImageUrl?: () => void;
}

export const PicturePreview: React.FC<ImagePreviewCardProps> = ({
  image,
  fileName,
  style,
  actions,
  cardWidth = PICTURE_PREVIEW_WIDTH,
  coverHeight = 200,
  onCardClick,
  meta = true,
  refreshImageUrl,
}) => {
  const imageUrl = useMemo(() => {
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }
    return image;
  }, [image]);

  // Cleanup object URL
  useEffect(() => {
    if (image instanceof File) {
      return () => URL.revokeObjectURL(imageUrl);
    }
  }, [image, imageUrl]);

  return (
    <HoverableCard
      style={{ ...style, width: cardWidth, overflow: "hidden" }}
      hoverable
      cover={
        imageUrl ? (
          <img
            src={imageUrl}
            alt="Preview"
            loading="lazy"
            onError={() => refreshImageUrl?.()}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        ) : null
      }
      styles={{
        cover: {
          height: coverHeight,
          overflowY: "hidden",
          display: "flex",
          borderRadius: meta ? "unset" : radius.box,
          margin: 0,
        },
        body: {
          display: meta ? "unset" : "none",
        },
        actions: { padding: 10 },
      }}
      size="small"
      actions={actions}
      onClick={() => {
        onCardClick?.();
      }}
    >
      {meta && (
        <Card.Meta
          description={
            <Tooltip title={fileName} arrow={false} placement="bottom">
              <Typography.Text
                style={{ width: "100%" }}
                ellipsis
                type="secondary"
              >
                {fileName}
              </Typography.Text>
            </Tooltip>
          }
        />
      )}
    </HoverableCard>
  );
};
